import { DocumentSummary } from "@/types/document";
import type { CPTCode, ICDTenCode } from "@/types/medical-codes";
import type { MedicationSummary } from "@/types/medication";
import { PageParams } from "@/types/pagination";
import type { PatientDetail, PatientSummary } from "@/types/patient";
import type { AttachmentStudioSummary } from "@/types/photo-studio";
import type { ProviderTiny } from "@/types/provider";
import type { TaskPriority } from "@/types/task";

// Note Question types (similar to form questions, including conditional visibility)
import { PatientDynamicFieldType } from "./enums/patient-health-field";
import {
  QuestionCondition,
  QuestionData,
  QuestionType,
} from "./forms/question";

type NotePatientDetailExtras = Pick<
  PatientDetail,
  | "memberships"
  | "goalWeight"
  | "measurements"
  | "medications"
  | "patientOptedOutOfSms"
  | "clinicOptedOutOfSms"
  | "appointmentCount"
  | "totalSpent"
>;

export type NotePatient = Omit<
  PatientSummary,
  keyof NotePatientDetailExtras
> & {
  [K in keyof NotePatientDetailExtras]?: NotePatientDetailExtras[K] | null;
};

export const notePatientToPatientSummary = (
  patient: NotePatient,
): PatientSummary => ({
  ...patient,
  memberships: patient.memberships ?? undefined,
});

export interface NoteTranscriptSummary {
  id: string;
  transcript: string;
  duration?: number;
  createdDate: string;
}

export type TelehealthBotLifecycleStatus =
  | "joining"
  | "waiting"
  | "listening"
  | "ended"
  | "transcribed"
  | "transcribe_failed"
  | "failed";

/**
 * Bot lifecycle for a telehealth appointment (ENG-2491).
 *
 * Embedded in `NoteDetail` via outer join (so every note response carries the
 * bot or `null`); live-patched by the ``TELEHEALTH_BOT_STATUS`` WS subscription
 * in `useTelehealthBotLiveUpdates`. All fields besides ``id`` and ``eventId``
 * are nullable — the row may exist before Recall accepts the create call.
 */
export interface TelehealthBot {
  id: string;
  eventId: string;
  /** FK to the Note the bot was started against. NULL while scheduled but
   *  unstarted. A mic note that shares the same event will see this row
   *  but its own id won't match — the recording-sync hook gates on this
   *  to avoid misclassifying mic-on-shared-event as telehealth. */
  noteId?: string | null;
  recallBotId?: string | null;
  recallBotStatus?: TelehealthBotLifecycleStatus | null;
  recallBotJoinAt?: string | null;
  /** Visit-ended timestamp (bot left the meeting). No longer the Generate
   *  gate — Generate unblocks once ``recallBotStatus`` reaches a
   *  transcript-terminal value (``transcribed`` / ``transcribe_failed`` /
   *  ``failed``). */
  recallBotEndedAt?: string | null;
}

export interface NoteBulkSignOffFailure {
  noteId: string;
  reason: string;
  patientName?: string | null;
}

export interface NoteBulkSignOffPreview {
  noteId: string;
  patientName?: string | null;
  title?: string | null;
}

/** Per-note outcome of POST /notes/bulk-sign-off. */
export interface NoteBulkSignOffResult {
  signed: string[];
  coSigned: string[];
  failed: NoteBulkSignOffFailure[];
  /** True when nothing was written: the result is what the batch would do. */
  dryRun?: boolean;
  /** The first few notes that will be (or were) signed, to name them. */
  preview?: NoteBulkSignOffPreview[];
}

/** A sign-off queue as seen by the signed-in user. */
export type NoteSignOffQueue = "mine" | "waiting_on_others";

/** Filters a "select all" bulk sign-off resolves its notes with, server-side. */
export type NoteListFilters = Pick<
  NotesFetchParams,
  "locationIds" | "templateId" | "patientName" | "patientId"
>;

/** Body of POST /notes/bulk-sign-off: explicit ids, or every note in a queue. */
export type NoteBulkSignOffRequest = { dryRun?: boolean } & (
  | { noteIds: string[] }
  | { queue: "mine"; filters?: NoteListFilters }
);

export interface NotesFetchParams extends PageParams {
  providerId?: string[];
  /** Providers on a service of the patient's appointment today. */
  seenTodayBy?: string[];
  templateId?: string[];
  patientName?: string;
  noteId?: string;
  patientId?: string;
  eventIds?: string[];
  createdDate?: string[];
  visitDate?: string[];
  locationIds?: string[];
  signedOff?: string[];
  queue?: NoteSignOffQueue;
}

export interface PrefilledAnswer {
  questionId: string;
  data: Record<string, unknown>;
  source?: string;
  isUserReported?: boolean;
}

export interface LastNotePrefill {
  noteId: string;
  visitDate: string;
  answers: PrefilledAnswer[];
}

export interface PrefilledAnswersResponse {
  templateId: string;
  patientId: string;
  prefilledAnswers: PrefilledAnswer[];
  lastNote: LastNotePrefill | null;
}

export interface DynamicFieldSummary {
  id: string;
  name: string;
  type: string | null;
  isCustom: boolean;
}

/** A generated answer the measurement field would not store. */
export interface SkippedMeasurement {
  questionId: string;
  fieldId: string;
  /** The field's own rejection text, e.g. "Weight must be at least 1.0". */
  message: string;
}

/**
 * What a generation attempt extracted but could not chart. Present only on the
 * response to that attempt — never on a note read back later.
 */
export interface NoteMappingSkips {
  measurements: SkippedMeasurement[];
}

export interface NoteDetail {
  id: string;
  eventId?: string;
  parentNoteId: string;
  duration?: number;
  version: number;
  currentVersion: number;
  summary?: string;
  title?: string;
  aiGenerated: boolean;
  template?: NoteTemplateTableItem;
  patient: NotePatient;
  createdDate: string;
  /** Linked appointment start when set; else first revision (detail API). */
  visitDate?: string;
  providers: ProviderTiny[];
  transcript?: NoteTranscriptSummary;
  providerId: string;
  sourceDocuments: DocumentSummary[];
  locationId?: string | null;
  icd10Codes?: ICDTenCode[];
  cptCodes?: CPTCode[];
  /** Per-CPT billing modifiers keyed by CPT code: { "99213": ["25"] }. */
  cptModifiers?: Record<string, string[]>;
  signedOffProviderId?: string;
  signedOffDate?: string;
  coSignedProviderId?: string;
  coSignedDate?: string;
  /** The note is signed but still owes the co-signature its template's rule
   * (or, with no rule, the clinic's prescriber setting) asks for. */
  requiresCoSign?: boolean;
  signOff?: NoteSignOffState | null;
  /** Open requests for someone to sign this note, oldest first. */
  pendingSignOffRequests?: PendingSignOffRequest[];
  /** The visit's required forms and their status, when the note's template
   * checks them and the note is linked to an appointment. Null otherwise. */
  requiredForms?: NoteRequiredForm[] | null;
  answers?: NoteAnswerDetail[]; // Structured answers for this note
  expiryDate?: string;
  isPinned?: boolean;
  /** Set when the author marked the note finished; sign-off completes it too.
   *  Only meaningful while the clinic requires completion. */
  completedDate?: string;
  completedByProviderId?: string;
  telehealthBot?: TelehealthBot | null;
  /** Set only by the generation endpoint; null on every read. */
  mappingSkips?: NoteMappingSkips | null;
  /**
   * AI-suggested follow-up tasks for this note's revision chain. Unlike
   * mappingSkips this is present on every read, so the strip survives a reload.
   * Null when the module or the template flag is off, or no run exists.
   */
  taskSuggestions?: NoteTaskSuggestionSet | null;
}

export type NoteTaskSuggestionRunStatus = "COMPLETED" | "FAILED";

export interface NoteTaskSuggestion {
  id: string;
  title: string;
  description?: string | null;
  priority: TaskPriority;
  dueInDays?: number | null;
  suggestedTeamId?: string | null;
  suggestedTeamName?: string | null;
  suggestedAssigneeId?: string | null;
  suggestedAssigneeName?: string | null;
  /** Verbatim transcript line the suggestion rests on, shown as the card caption. */
  evidence?: string | null;
  dismissedAt?: string | null;
  /** Set once accepted — the card flips to its terminal "Task created" state. */
  createdTaskId?: string | null;
}

export interface NoteTaskSuggestionSet {
  id: string;
  status: NoteTaskSuggestionRunStatus;
  errorMessage?: string | null;
  attemptCount: number;
  canRetry: boolean;
  suggestions: NoteTaskSuggestion[];
}

export interface NoteListItem {
  id: string;
  eventId?: string;
  /** Linked Super Bill, so the row can offer "Go to Super Bill" with no lookup. */
  superbillId?: string | null;
  summary?: string;
  title?: string;
  templateId?: string;
  createdDate: string;
  visitDate: string; // The original created date (first version)
  lastEditedDate: string; // The most recent created date in the version chain
  patient: PatientSummary;
  providers: ProviderTiny[];
  version: number;
  aiGenerated: boolean;
  sourceDocuments: DocumentSummary[];
  signedOffProviderId?: string;
  signedOffDate?: string;
  coSignedProviderId?: string;
  coSignedDate?: string;
  /** The note still owes a prescriber's signature: the tenant requires co-sign,
   * the author is not a prescriber, and no prescriber has signed it. */
  requiresCoSign?: boolean;
  hasAnswers: boolean; // Whether the note has non-archived answers (indicates completion)
  answers?: NoteAnswerDetail[]; // Raw answers for AI context
  questions?: NoteQuestionDetail[]; // Questions for formatting answers
  /** Populated when a detail record is prepared for printing. */
  locationId?: string | null;
  /** Populated when a detail record is prepared for printing. */
  icd10Codes?: ICDTenCode[];
  /** Populated when a detail record is prepared for printing. */
  cptCodes?: CPTCode[];
  /** Per-CPT billing modifiers keyed by CPT code. */
  cptModifiers?: Record<string, string[]>;
  expiryDate?: string;
  isPinned?: boolean;
  /** Set when the author marked the note finished; sign-off completes it too.
   *  Only meaningful while the clinic requires completion. */
  completedDate?: string;
  completedByProviderId?: string;
  /** Open "Request sign-off" tasks on this note's chain. Empty once signed. */
  pendingSignOffRequests?: PendingSignOffRequest[];
  /** The provider on this version: the author whose signature the note waits on. */
  authorId?: string | null;
  signedOffProviderName?: string | null;
  coSignedProviderName?: string | null;
  /** When the note started waiting on its next signature. Null once complete. */
  waitingSince?: string | null;
  signOff?: NoteSignOffState | null;
}

/** One form a note's visit requires, and where the patient stands on it. */
export interface NoteRequiredForm {
  formId: string;
  name: string;
  status: "COMPLETED" | "EXPIRED" | "IN_PROGRESS" | "NOT_STARTED";
  completedDate?: string | null;
}

export type NoteFirstSigner = "ANYONE" | "AUTHOR" | "ROLE";
export type NoteCoSigner = "NONE" | "PRESCRIBER" | "ROLE";

/** How a note's sign-off rule reads for it, and what the viewer may do. */
export interface NoteSignOffState {
  firstSigner: NoteFirstSigner;
  /** The role that gives the first signature, when the rule names one. */
  firstSignerLabel?: string | null;
  /** The co-signature in play: owed now or, before the first signature, the one
   * the rule will ask for. A role's name or "Any prescriber". */
  coSignerLabel?: string | null;
  /** The viewer may give the first signature. */
  viewerCanSign: boolean;
  /** The viewer is in the co-signer group. */
  viewerCanCoSign: boolean;
}

/** Who has to sign notes from a template. Null on a template means it follows the clinic setting. */
export interface NoteTemplateSignOffRule {
  firstSigner: NoteFirstSigner;
  firstSignerRoleId?: string | null;
  coSigner: NoteCoSigner;
  coSignerRoleId?: string | null;
  /** Authors whose notes owe the co-signature; null = every author outside the co-signer group. */
  coSignAuthorRoleIds?: string[] | null;
}

/** A group a sign-off rule can name, with who is in it now. */
export interface NoteSignOffSignerGroup {
  /** Null for "Any prescriber". */
  id: string | null;
  name: string;
  isAdminRole: boolean;
  memberNames: string[];
}

/** Someone whose notes sign themselves when they save them ("Auto sign-off notes"). */
export interface NoteAutoSignOffProvider {
  id: string;
  name: string;
  /** Their active roles, to tell which of them a role-first rule still signs automatically. */
  roleIds: string[];
}

/** Someone a sign-off request can go to: they may give the note's next signature. */
export interface NoteSignOffCandidate {
  id: string;
  name: string;
  roleNames: string[];
}

export interface PendingSignOffRequest {
  taskId: string;
  /** The provider asked, or null for a request to a team. */
  assigneeId?: string | null;
  /** Assigned provider's name, else the team name. */
  assigneeName?: string | null;
}

export interface NoteAnswerDetail {
  id: string;
  noteId: string;
  questionId: string;
  data: Record<string, any>; // Answer value(s) - format depends on question_type
  measurementValueId?: string | null;
  isUserReported?: boolean;
  createdDate: string;
}

export interface NoteScribeResponse {
  id: string;
  createdDate: string;
  answers: NoteAnswerDetail[];
  template?: NoteTemplateTableItem;
}

export interface NoteTranscriptionResponse {
  id: string;
  noteId: string;
  transcript?: string;
  createdDate: string;
}

export interface NoteSectionDetail {
  id: string;
  templateId: string;
  name: string;
  position: number;
  columns: number;
  measurementGroupId?: string | null;
  isArchived: boolean;
  /** When set, the whole section shows only if every rule passes. */
  conditions?: QuestionCondition[];
}

export interface NoteSectionCreate {
  id?: string;
  name: string;
  position: number;
  columns?: number;
  measurementGroupId?: string | null;
  conditions?: QuestionCondition[];
}

export interface NoteQuestionDetail<T extends QuestionData = QuestionData> {
  id: string;
  templateId: string;
  position: number;
  questionType: QuestionType;
  required: boolean;
  isArchived: boolean;
  mappingKey?: string;
  dynamicFieldId?: string;
  dynamicFieldName?: string;
  measurementFieldId?: string;
  measurementFieldName?: string;
  sectionId?: string;
  data: T;
  /** When set, the question shows only if every rule passes. */
  conditions?: QuestionCondition[];
}

export interface NoteQuestionCreate {
  id?: string; // If provided, updates existing question; if omitted, creates new question
  /**
   * Client-stable key for this question within the payload, referenced by
   * `conditions[].dependentQuestionId`. Never persisted: the backend mints row ids,
   * and any edit (even a reorder) recreates the row, so the client's own key is the
   * only thing stable enough to resolve conditions against.
   */
  tempId?: string;
  templateId: string;
  position: number;
  questionType: QuestionType;
  required?: boolean;
  mappingKey?: string;
  dynamicFieldId?: string;
  measurementFieldId?: string;
  sectionId?: string;
  data: QuestionData;
  conditions?: QuestionCondition[];
}

export interface NoteTemplateTableItem {
  id: string;
  name: string;
  /** The enterprise's record this one follows, if any. */
  enterpriseRef?: string | null;
  sections?: NoteSectionDetail[];
  questions: NoteQuestionDetail[];
  userIds?: string[];
  isFavorited?: boolean;
  createdDate: string;
  updatedDate?: string | null;
  /** Monotonic per-template revision used to order realtime snapshots. */
  mutationRevision?: number;
  expiryDays?: number | null;
  /** Whether notes from this template are shown in the patient portal. */
  visibleToPatient?: boolean;
  /** Ordered list of question IDs the patient sees; null/undefined means all in template order. */
  patientVisibleQuestionOrder?: string[] | null;
  /** Whether the note summary is shown in the patient portal view. */
  showSummaryInPatientView?: boolean;
  /** Whether notes from this template surface the Super Bill tab / default to billable. */
  superbillEnabled?: boolean;
  taskSuggestionsEnabled?: boolean;
  /** Show the visit's required forms, and which are missing, on the note page. */
  requiredFormsCheckEnabled?: boolean;
  /** Instructions for AI note titles; null uses the standard title. */
  titlePrompt?: string | null;
  /** Null when the template follows the clinic setting. */
  signOffRule?: NoteTemplateSignOffRule | null;
}

export interface NoteCountByDate {
  createdDate: string;
  count: number;
}

export interface NoteGroupItem {
  key: string;
  label?: string;
  total: number;
  /**
   * Position in a grouped-table tree (0 = root). Defaults to 0 for surfaces
   * that don't return hierarchical groups.
   */
  depth?: number;
  /**
   * Key of this group's parent in a grouped-table tree. Null/undefined at the
   * root, or when the grouping is flat (no tree).
   */
  parentKey?: string | null;
  /**
   * True when this row is a synthetic ancestor with no items of its own —
   * the orchestrator skips its per-group listQuery and the body renders only
   * the header. A leaf-and-parent (items of its own AND children with items)
   * leaves this false so its rows still render.
   */
  isSynthetic?: boolean;
}

export interface NoteSummary {
  id: string;
  patientNames: string[];
  providerNames: string[];
  version: number;
  createdDate: string;
  signedOffProviderId?: string;
  signedOffDate?: string;
}

/** The newest saved version of a note, and who saved it. */
export interface NoteLatestRevision {
  id: string;
  version: number;
  createdDate: string;
  provider?: ProviderTiny | null;
}

export interface NoteAnswerCreateWithoutNoteId {
  questionId: string;
  data: Record<string, any>;
  noteAttachmentId?: string;
  isUserReported?: boolean;
}

export interface NoteCreate {
  patientId: string;
  providerId: string;
  parentNoteId?: string;
  eventId?: string | null;
  aiGenerated?: boolean;
  title?: string;
  noteTranscriptId?: string;
  template?: NoteTemplateTableItem;
  locationId?: string | null;
  icd10Codes?: string[];
  cptCodes?: string[];
  /** Per-CPT billing modifiers keyed by CPT code: { "99213": ["25"] }. */
  cptModifiers?: Record<string, string[]> | null;
  answers?: NoteAnswerCreateWithoutNoteId[];
  /** The version the editor loaded, so the save merges onto any newer one. */
  baseNoteId?: string;
  /** A part both this provider and a colleague changed: refuse, keep mine, or keep theirs. */
  onConflict?: NoteSaveConflictChoice;
}

export type NoteSaveConflictChoice = "reject" | "mine" | "theirs";

/** One part of the note that a colleague also changed since it was opened. */
export interface NoteSaveConflictField {
  field:
    | "title"
    | "event_id"
    | "icd10_codes"
    | "cpt_codes"
    | "cpt_modifiers"
    | "template"
    | "answer";
  /** snake_case on purpose: error details are not camelized. */
  question_id?: string;
}

/** The 409 body `notes/create` returns when the save hits a colleague's changes. */
export interface NoteSaveConflict {
  code: "note_save_conflict";
  message: string;
  conflicts: NoteSaveConflictField[];
  latest_note_id: string;
  saved_by?: { id: string; first_name: string; last_name?: string } | null;
}

export interface NoteUpdate extends NoteCreate {
  noteTranscriptId?: string;
}

export interface NotesAndDocumentsFetchParams extends PageParams {
  itemType?: "note" | "document";
  documentType?: string;
}

export interface DocumentTableItem {
  id: string;
  filePath: string;
  fileType: string;
  title: string;
  summary?: string;
  docType: string;
  createdDate: string;
}

export interface NoteTemplateCreate {
  name: string;
  sections?: NoteSectionCreate[];
  questions: NoteQuestionCreate[];
  userIds?: string[] | null; // null = everyone has access, [ids] = specific users
  expiryDays?: number | null;
  /** Whether notes from this template surface the Super Bill tab / default to billable. */
  superbillEnabled?: boolean;
  taskSuggestionsEnabled?: boolean;
  requiredFormsCheckEnabled?: boolean;
  signOffRule?: NoteTemplateSignOffRule | null;
  titlePrompt?: string | null;
}

export interface NoteTemplateUpdate {
  id: string;
  name?: string;
  sections?: NoteSectionCreate[];
  questions: NoteQuestionCreate[];
  userIds?: string[] | null; // null = everyone has access, [ids] = specific users, undefined = don't change
  expiryDays?: number | null;
  /** Whether notes from this template surface the Super Bill tab / default to billable. Omit to leave unchanged. */
  superbillEnabled?: boolean;
  taskSuggestionsEnabled?: boolean;
  requiredFormsCheckEnabled?: boolean;
  /** Omit to leave unchanged; null returns the template to the clinic setting. */
  signOffRule?: NoteTemplateSignOffRule | null;
  /** Omit to leave unchanged; null or blank returns to the standard title. */
  titlePrompt?: string | null;
}

export interface NoteAttachmentNote {
  id: string;
  createdDate?: string;
  visitDate?: string | null;
  eventId?: string | null;
  eventTitle?: string | null;
}

export interface NoteAttachmentTag {
  id: string;
  name: string;
  color: string;
  createdDate?: string;
}

export interface NoteAttachment {
  id: string;
  // API response fields
  noteId?: string | null;
  gcsPath?: string;
  originalFilename?: string;
  signedUrl?: string; // Signed URL from API (preferred)
  thumbnailSignedUrl?: string | null;

  // Client-side fields (for uploads and placeholders)
  url?: string; // Either blob URL or signed URL (use signedUrl if available, fallback to url)
  thumbnailUrl?: string; // Thumbnail URL (use thumbnailSignedUrl if available, fallback to thumbnailUrl)
  attachmentId?: string; // Backend ID when uploaded (alias for id)
  file?: File; // Only for new uploads

  isPlaceholder?: boolean; // Indicates if this is a template placeholder

  // Common fields
  annotations?: any;
  name?: string;
  createdDate?: string;
  // Raw upload timestamp (with time), distinct from createdDate which the gallery
  // overrides with the linked note's visit date. Used to show a per-photo time.
  uploadedAt?: string;
  tags?: NoteAttachmentTag[];
  patient?: PatientSummary;
  note?: NoteAttachmentNote;
  provider?: ProviderTiny;
  // Patient portal sharing: whether this photo is shared to the patient's
  // portal gallery, and the optional patient-facing caption.
  visibleToPatient?: boolean;
  patientCaption?: string | null;
  // Only returned by the annotation apply-diff endpoint: the doses the server
  // just recomputed from face-chart marks, so the client can seed them into the
  // patient chart cache instead of waiting for a refetch.
  createdDoses?: MedicationSummary[] | null;
  archivedDoseIds?: string[] | null;
  // Studio Mode state.
  studio?: AttachmentStudioSummary | null;
}

// Alias for API responses that match the attachment structure
export type NoteAttachmentResponse = NoteAttachment;

export interface TemplateAttachmentUploadResponse {
  gcsPath: string;
  signedUrl: string;
  originalFilename: string;
}

export interface AttachmentDayPage {
  date: string;
  data: NoteAttachment[];
  nextDate?: string | null;
}

export interface NoteAnswerWithMapping {
  answer: NoteAnswerDetail;
  question: NoteQuestionDetail;
  mappingKey?: string;
  mappedValue?: Record<string, any>; // Processed value ready for database
}

export interface NoteMappingsResponse {
  noteId: string;
  mappings: NoteAnswerWithMapping[];
}

export interface ConfirmMappingsRequest {
  noteId: string;
  confirmedMappingIds: string[]; // List of answer IDs to process
}

export interface ImageTransform {
  panX: number; // Relative horizontal offset (-1 to 1)
  panY: number; // Relative vertical offset (-1 to 1)
  zoom: number; // Zoom factor (1 = 100%)
  rotation?: number; // Rotation in degrees (0-360)
  cropX?: number | null; // Crop region left edge (0-1 relative)
  cropY?: number | null; // Crop region top edge (0-1 relative)
  cropWidth?: number | null; // Crop region width (0-1 relative)
  cropHeight?: number | null; // Crop region height (0-1 relative)
  mirror?: boolean; // Horizontal flip
}

// Template Migration Helper types
// Used for LLM-powered conversion of legacy TEXT_AREA templates to structured forms

/**
 * Prefix used to identify legacy migrated questions.
 * Questions with descriptions starting with this prefix were created by the
 * scribe_as_a_form migration and contain the old content_template.
 */
export const LEGACY_TEMPLATE_DESCRIPTION_PREFIX =
  "Extract and format the note content following this template structure:\n";

/**
 * A single question generated by the LLM migration helper.
 * Represents a structured question inferred from analyzing legacy template content.
 */
export interface GeneratedQuestion {
  tempId?: string;
  position?: number;
  questionType: QuestionType;
  name: string;
  description?: string;
  options?: string[];
  content?: string;
  required: boolean;
  patientHealthData?: PatientDynamicFieldType;
  mappingKey?: string;
  sectionId?: string;
  /** temp_id (or section name) of the item this one should sit right after. */
  after?: string;
  /** temp_id (or section name) of the item this one should sit right before. */
  before?: string;
  /**
   * Visibility rules the model authored. Note templates only, and validated by
   * `sanitizeConditions` before use -- this is raw model output, so it is typed
   * as unknown rather than trusted into a shape.
   */
  conditions?: unknown;
}
